import ShareIcon from "@mui/icons-material/Share";
import { Box, styled, Typography, Popover } from "@mui/material";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton,
  XIcon,
} from "react-share";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import copyToClipboard from "copy-to-clipboard";
import { useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import { WEB_URL_BASE } from "@/app/layout/LayoutConstants";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";

const SHARE_ICON_SIZE = 32;

export interface AdShareModel {
  id: string;
  title: string;
  descriptionShort: string;
  url: string;
  sellerId: string;
}

const List = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    disaply: "flex",
    flexDirection: "column",
    padding: theme.spacing(1, 0),
  })
);

const Item = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    "> *": {
      display: "flex",
      alignItems: "center",
      gap: 2,
      cursor: "pointer",
      width: "100%",
      padding: theme.spacing(1, 2) + " !important",
    },
    "&:hover": {
      backgroundColor: "grey.100",
    },
    ".MuiSvgIcon-root": {
      width: SHARE_ICON_SIZE,
      height: SHARE_ICON_SIZE,
      backgroundColor: "grey.300",
      padding: 1,
      borderRadius: "100px",
    },
  })
);

function TrackShareClick({
  ad,
  provider,
}: {
  ad: AdShareModel;
  provider: ShareProvider;
}) {
  CreateAnalyticEvent({
    event: {
      adIds: [ad.id],
      eventType: "AD#ENGAGEMENT#SHARE#CLICK",
      metadata: {
        provider: provider,
      },
    },
  });
}

function ExternalShareItem({
  ad,
  provider,
  children,
}: {
  ad: AdShareModel;
  provider: ShareProvider;
  children: React.ReactNode;
}) {
  return (
    <Item onClick={() => TrackShareClick({ ad, provider })}>{children}</Item>
  );
}

function CopyLinkButton({
  ad,
  shareUrl,
}: {
  ad: AdShareModel;
  shareUrl: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOnClick = (event: React.MouseEvent<HTMLElement>) => {
    const wasCopied = copyToClipboard(shareUrl);
    if (wasCopied) {
      setAnchorEl(event.currentTarget);
    }
    TrackShareClick({ ad, provider: "COPY" });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box
          sx={{
            padding: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              display: "flex",
              gap: 1,
              fontWeight: "bold",
            }}
          >
            <CheckIcon />
            Copied to clipboard
          </Typography>
          <Typography variant="body2">
            <a href={shareUrl} target="_blank" rel="noreferrer">
              {shareUrl}
            </a>
          </Typography>
        </Box>
      </Popover>
      <Item onClick={handleOnClick}>
        <Box>
          <LinkOutlinedIcon />
          <Typography>Copy Link</Typography>
        </Box>
      </Item>
    </>
  );
}

function NativeShareButton({
  ad,
  shareUrl,
}: {
  ad: AdShareModel;
  shareUrl: string;
}) {
  return (
    <Item
      onClick={() => {
        navigator.share({
          url: shareUrl,
          text: ad.title,
          title: ad.title,
        });
        TrackShareClick({ ad, provider: "NATIVE" });
      }}
    >
      <Box>
        <ShareIcon />
        <Typography>More</Typography>
      </Box>
    </Item>
  );
}

type ShareProvider =
  | "FACEBOOK"
  | "WHATSAPP"
  | "TWITTER"
  | "EMAIL"
  | "COPY"
  | "NATIVE";

export function ShareList({ ad }: { ad: AdShareModel }) {
  // AJB: no longer just for aspects
  const shareUrl = `${WEB_URL_BASE}/?a=2&b=${ad.id}`; // AJB: extract const for web url
  const shareHashTag = "horsezone";

  return (
    <List>
      <ExternalShareItem ad={ad} provider="FACEBOOK">
        <FacebookShareButton url={shareUrl} hashtag={shareHashTag}>
          <FacebookIcon round size={SHARE_ICON_SIZE} />
          <Typography>Facebook</Typography>
        </FacebookShareButton>
      </ExternalShareItem>
      <ExternalShareItem ad={ad} provider="WHATSAPP">
        <WhatsappShareButton url={shareUrl} title={ad.title}>
          <WhatsappIcon round size={SHARE_ICON_SIZE} />
          <Typography>WhatsApp</Typography>
        </WhatsappShareButton>
      </ExternalShareItem>
      <ExternalShareItem ad={ad} provider="TWITTER">
        <TwitterShareButton
          title={ad.title}
          url={shareUrl}
          hashtags={[shareHashTag]}
        >
          <XIcon round size={SHARE_ICON_SIZE} />
          <Typography>X</Typography>
        </TwitterShareButton>
      </ExternalShareItem>
      <ExternalShareItem ad={ad} provider="EMAIL">
        <EmailShareButton
          url={shareUrl}
          body={ad.descriptionShort}
          subject={ad.title}
        >
          <EmailIcon round size={SHARE_ICON_SIZE} />
          <Typography>Email</Typography>
        </EmailShareButton>
      </ExternalShareItem>
      <CopyLinkButton ad={ad} shareUrl={shareUrl} />
      <NativeShareButton ad={ad} shareUrl={shareUrl} />
    </List>
  );
}
